// client/src/components/pages/LexiStar/Routes/RouteEditor.jsx
//
// Route editor (LS-09 §5.11; FR-ROUT-001…006): the map on the left, the panel
// on the right with three tabs —
//   Edit        where the line comes from (engine with a truck profile and
//               drag-to-adjust via points, or a golden run from a trip, trimmed),
//               corridor width with a live preview, required weighbridges and
//               permitted stops (tick them, or click the zone on the map),
//               planned distance and time, risk, toll plazas, version note
//   Versions    immutable history; compare on the map; start from an old one
//   Advisories  time-boxed notices with an optional alternate corridor; retract
// Saving an existing route sends only what changed; the server copies the rest
// forward into the next version (trips keep the version they were dispatched on).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaMagic, FaHistory, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import SegmentedControl from '../../../common/SegmentedControl';
import ThemedSelect from '../../../common/ThemedSelect';
import {
  getRoute,
  getCorridor,
  createRoute,
  createVersion,
  updateRoute,
  generateRoute,
} from '../../../../services/lexistarRoutes';
import RouteMap from './RouteMap';
import { GeneratePanel, FromTripPanel, generateRequest, DEFAULT_VEHICLE } from './GeometryTools';
import VersionsPanel from './VersionsPanel';
import AdvisoriesPanel from './AdvisoriesPanel';
import RecomputeOffer from '../RecomputeJobs/RecomputeOffer'; // M3-U1
import { StatusPill } from './RouteCards';
import { laneLabel } from './LaneList';
import {
  CORRIDOR_MIN_M,
  CORRIDOR_MAX_M,
  STOP_PURPOSES,
  draftFromVersion,
  versionPayload,
  hasVersionChanges,
  geofencePurpose,
  toggleId,
  formatKm,
  formatMinutes,
} from './routeGeometry';

const SOURCE_LABELS = {
  engine: 'Routing engine',
  golden_run: 'Golden run',
  manual: 'Manual',
  import: 'Import',
};

const inputClass =
  'w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-slate-50 disabled:text-slate-500';

function Section({ title, hint, children }) {
  return (
    <section className="space-y-2">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function GeofenceChecklist({ items, selected, onToggle, disabled, empty }) {
  if (!items.length) return <p className="text-xs text-slate-500">{empty}</p>;
  return (
    <ul className="max-h-40 overflow-y-auto space-y-1 pr-1">
      {items.map((g) => {
        const id = Number(g.GeofenceID);
        return (
          <li key={id}>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={selected.includes(id)}
                onChange={() => onToggle(id)}
                disabled={disabled}
              />
              <span className="truncate">{g.Name}</span>
              <span className="ml-auto text-[11px] text-slate-400">
                {geofencePurpose(g).replace(/_/g, ' ')}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

export default function RouteEditor({
  lane,
  routeId,
  geofences,
  tollPlazas,
  canManage,
  canAdvise,
  onSaved,
  onCreated,
  onCancelNew,
}) {
  const isNew = !routeId;
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [loadError, setLoadError] = useState(null);
  const [draft, setDraft] = useState(() => draftFromVersion(null));
  const [meta, setMeta] = useState({ name: '', code: '', status: 'approved' });
  const [tab, setTab] = useState('edit');
  const [tool, setTool] = useState(isNew ? 'generate' : null);
  const [mapMode, setMapMode] = useState(null); // 'via' | 'from' | 'to' | 'altVia'
  const [via, setVia] = useState([]);
  const [points, setPoints] = useState({ from: null, to: null });
  const [ends, setEnds] = useState({ from: null, to: null });
  const [vehicle, setVehicle] = useState(DEFAULT_VEHICLE);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [lastGenerate, setLastGenerate] = useState(null);
  const [track, setTrack] = useState(null);
  const [compareVersion, setCompareVersion] = useState(null);
  const [savedCorridor, setSavedCorridor] = useState(null);
  const [altVia, setAltVia] = useState([]);
  const [altLine, setAltLine] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const route = detail?.route || null;
  const base = detail?.current_version || null;
  const canChange = isNew ? canManage : !!(canManage && detail?.can_change);
  const canPublishAdvisory = !isNew && !!(detail?.can_change && (canManage || canAdvise));

  const load = useCallback(async () => {
    if (!routeId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const body = await getRoute(routeId);
      setDetail(body);
      setDraft(draftFromVersion(body.current_version));
      setMeta({ name: body.route.name, code: body.route.code, status: body.route.status });
      setCompareVersion(null);
      setTrack(null);
      setVia([]);
      setSavedCorridor(null);
      if (body.current_version) {
        getCorridor(routeId, body.current_version.version)
          .then(setSavedCorridor)
          .catch(() => setSavedCorridor(null));
      }
    } catch (err) {
      setLoadError(err.response?.data?.error || 'Could not load this route.');
    } finally {
      setLoading(false);
    }
  }, [routeId]);

  useEffect(() => {
    load();
  }, [load]);

  // New route: suggest a name from the lane.
  useEffect(() => {
    if (isNew) {
      setMeta({ name: laneLabel(lane), code: '', status: 'approved' });
      setDraft(draftFromVersion(null));
      setTool('generate');
      setVia([]);
      setPoints({ from: null, to: null });
      setEnds({ from: null, to: null });
      setTrack(null);
    }
  }, [isNew, lane]);

  const line = draft.geometry?.coordinates || null;
  const markerEnds = {
    from: points.from || (line ? line[0] : ends.from),
    to: points.to || (line ? line[line.length - 1] : ends.to),
  };

  const payload = useMemo(
    () => (isNew || !base ? versionPayload(draft) : versionPayload(draft, base)),
    [draft, base, isNew]
  );
  const dirty = isNew || !base ? !!draft.geometry : hasVersionChanges(payload);

  const compareLine = useMemo(() => {
    if (compareVersion == null || !detail) return null;
    const v = detail.versions.find((x) => x.version === compareVersion);
    return v?.geometry?.coordinates || null;
  }, [compareVersion, detail]);

  const alternates = useMemo(() => {
    const list = (detail?.advisories || [])
      .filter(
        (a) => a.alt_corridor?.geometry && ['active', 'scheduled'].includes(a.effective_state)
      )
      .map((a) => ({
        coords: a.alt_corridor.geometry.coordinates,
        corridor_m: a.alt_corridor.corridor_m,
      }));
    if (altLine) list.push({ coords: altLine, corridor_m: draft.corridor_m, draft: true });
    return list;
  }, [detail, altLine, draft.corridor_m]);

  const weighbridges = useMemo(
    () => geofences.filter((g) => geofencePurpose(g) === 'weighbridge'),
    [geofences]
  );
  const stopCandidates = useMemo(() => {
    const rank = (g) => {
      const i = STOP_PURPOSES.indexOf(geofencePurpose(g));
      return i === -1 ? STOP_PURPOSES.length : i;
    };
    return geofences
      .filter((g) => geofencePurpose(g) !== 'weighbridge' && geofencePurpose(g) !== 'no_go')
      .sort((a, b) => rank(a) - rank(b) || String(a.Name).localeCompare(String(b.Name)));
  }, [geofences]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  // ── Engine ───────────────────────────────────────────────────────────────

  const runGenerate = useCallback(
    async (viaOverride) => {
      setGenerating(true);
      setGenerateError(null);
      try {
        const res = await generateRoute(
          generateRequest(lane, { via: viaOverride || via, points, vehicle })
        );
        setLastGenerate(res);
        setEnds({ from: res.from?.point || null, to: res.to?.point || null });
        setTrack(null);
        setDraft((d) => ({
          ...d,
          geometry: res.geometry,
          planned_km: res.planned_km,
          planned_min: res.planned_min,
          source: 'engine',
          source_trip_id: null,
        }));
      } catch (err) {
        setGenerateError(
          err.response?.data?.error || 'The routing engine could not produce a route.'
        );
      } finally {
        setGenerating(false);
      }
    },
    [lane, via, points, vehicle]
  );

  const onGoldenRun = (res) => {
    setDraft((d) => ({
      ...d,
      geometry: res.geometry,
      planned_km: res.planned_km,
      planned_min: res.planned_min,
      source: 'golden_run',
      source_trip_id: res.source_trip_id,
    }));
  };

  // ── Map interaction ──────────────────────────────────────────────────────

  const onMapPoint = (p) => {
    if (mapMode === 'via') setVia((v) => [...v, p]);
    else if (mapMode === 'altVia') setAltVia((v) => [...v, p]);
    else if (mapMode === 'from' || mapMode === 'to') {
      setPoints((prev) => ({ ...prev, [mapMode]: p }));
      setMapMode(null);
    }
  };

  const onMoveVia = (i, p) => {
    const next = via.map((q, j) => (j === i ? p : q));
    setVia(next);
    runGenerate(next);
  };

  const onGeofenceClick = (id, purpose) => {
    if (!canChange || tab !== 'edit') return;
    setDraft((d) => {
      if (purpose === 'weighbridge') {
        return {
          ...d,
          required_weighbridge_geofence_ids: toggleId(d.required_weighbridge_geofence_ids, id),
          permitted_stop_geofence_ids: d.permitted_stop_geofence_ids.filter((x) => x !== id),
        };
      }
      return {
        ...d,
        permitted_stop_geofence_ids: toggleId(d.permitted_stop_geofence_ids, id),
        required_weighbridge_geofence_ids: d.required_weighbridge_geofence_ids.filter(
          (x) => x !== id
        ),
      };
    });
  };

  // ── Save ─────────────────────────────────────────────────────────────────

  const save = async () => {
    setError(null);
    setNotice(null);
    if (!draft.geometry) return setError('Generate or load the route line first.');
    if (isNew && !meta.name.trim()) return setError('Give the route a name.');
    setSaving(true);
    try {
      if (isNew) {
        const body = await createRoute({
          lane_id: lane.id,
          name: meta.name.trim(),
          code: meta.code.trim() || undefined,
          status: meta.status,
          version: payload,
        });
        onCreated(body.route);
      } else {
        const body = await createVersion(routeId, payload);
        setNotice(`Saved version ${body.version?.version ?? ''}.`.replace(' .', '.'));
        await load();
        onSaved();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the route.');
      const details = err.response?.data?.details;
      if (details?.length) setError(`${err.response.data.error}: ${details.join('; ')}`);
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status) => {
    setError(null);
    try {
      await updateRoute(routeId, { status });
      setNotice(
        status === 'retired'
          ? 'Route retired: new trips can no longer use it.'
          : `Route marked ${status}.`
      );
      await load();
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not change the route status.');
    }
  };

  const rename = async () => {
    if (!route || meta.name.trim() === route.name || !meta.name.trim()) return;
    try {
      await updateRoute(routeId, { name: meta.name.trim() });
      await load();
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not rename the route.');
    }
  };

  const startFromVersion = (v) => {
    setDraft({ ...draftFromVersion(v), notes: `Restored from version ${v.version}` });
    setCompareVersion(null);
    setTab('edit');
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-500">
        Loading route…
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
        {loadError}
      </div>
    );
  }

  const tabs = [
    { value: 'edit', label: isNew ? 'New route' : 'Edit' },
    ...(isNew
      ? []
      : [
          { value: 'versions', label: 'Versions', badge: detail?.versions?.length || 0 },
          {
            value: 'advisories',
            label: 'Advisories',
            badge: (detail?.advisories || []).filter((a) =>
              ['active', 'scheduled'].includes(a.effective_state)
            ).length,
          },
        ]),
  ];

  return (
    <div className="bg-surface rounded-xl border border-slate-200 shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          {isNew ? (
            <p className="text-sm font-semibold text-slate-900">New route on {laneLabel(lane)}</p>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-xs font-semibold text-slate-600">{route.code}</span>
              {canChange ? (
                <input
                  aria-label="Route name"
                  value={meta.name}
                  onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))}
                  onBlur={rename}
                  maxLength={120}
                  className="min-w-0 flex-1 text-sm font-semibold text-slate-900 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-primary-500 focus:outline-none"
                />
              ) : (
                <span className="text-sm font-semibold text-slate-900 truncate">{route.name}</span>
              )}
              <StatusPill status={route.status} />
              {route.is_primary && (
                <span className="text-[11px] font-semibold uppercase tracking-wide text-primary-700">
                  Primary
                </span>
              )}
            </div>
          )}
        </div>
        {!isNew && canChange && (
          <div className="flex gap-2">
            {route.status === 'proposed' && (
              <button
                type="button"
                onClick={() => changeStatus('approved')}
                className="px-2.5 py-1.5 text-xs font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
              >
                Approve
              </button>
            )}
            {route.status === 'retired' ? (
              <button
                type="button"
                onClick={() => changeStatus('approved')}
                className="px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200"
              >
                Restore
              </button>
            ) : (
              <button
                type="button"
                onClick={() => changeStatus('retired')}
                className="px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200"
              >
                Retire
              </button>
            )}
          </div>
        )}
        {isNew && (
          <button
            type="button"
            onClick={onCancelNew}
            className="px-2.5 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
          >
            Cancel
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_24rem] gap-4 p-4">
        <RouteMap
          geofences={geofences}
          stopIds={draft.permitted_stop_geofence_ids}
          weighbridgeIds={draft.required_weighbridge_geofence_ids}
          line={line}
          corridorM={Number(draft.corridor_m) || 0}
          savedCorridor={
            base && Number(draft.corridor_m) === Number(base.corridor_m) && !payload.geometry
              ? savedCorridor
              : null
          }
          compareLine={compareLine}
          track={track}
          alternates={alternates}
          from={markerEnds.from}
          to={markerEnds.to}
          via={tab === 'advisories' ? altVia : via}
          addingVia={!!mapMode}
          onGeofenceClick={onGeofenceClick}
          onAddVia={onMapPoint}
          onMoveVia={tab === 'advisories' ? null : canChange ? onMoveVia : null}
        />

        <div className="space-y-4 min-w-0">
          <SegmentedControl
            options={tabs}
            value={tab}
            onChange={(t) => {
              setTab(t);
              setMapMode(null);
            }}
            ariaLabel="Route editor"
          />

          {tab === 'edit' && (
            <div className="space-y-5">
              {isNew && (
                <Section title="Route">
                  <label className="block text-xs text-slate-600">
                    Name
                    <input
                      className={inputClass}
                      value={meta.name}
                      onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))}
                      maxLength={120}
                    />
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block text-xs text-slate-600">
                      Code <span className="text-slate-400">(optional)</span>
                      <input
                        className={inputClass}
                        value={meta.code}
                        onChange={(e) => setMeta((m) => ({ ...m, code: e.target.value }))}
                        maxLength={40}
                        placeholder="Suggested on save"
                      />
                    </label>
                    <div>
                      <label htmlFor="route-status" className="block text-xs text-slate-600">
                        Status
                      </label>
                      <ThemedSelect
                        id="route-status"
                        value={meta.status}
                        onChange={(e) => setMeta((m) => ({ ...m, status: e.target.value }))}
                      >
                        <option value="approved">Approved</option>
                        <option value="proposed">Proposed</option>
                      </ThemedSelect>
                    </div>
                  </div>
                </Section>
              )}

              <Section title="Line">
                {line ? (
                  <p className="text-sm text-slate-800 tabular-nums">
                    {formatKm(draft.planned_km)} · {formatMinutes(draft.planned_min)}
                    <span className="ml-2 text-xs text-slate-500">
                      {SOURCE_LABELS[draft.source] || draft.source}
                      {payload.geometry && !isNew ? ' · not saved yet' : ''}
                    </span>
                  </p>
                ) : (
                  <p className="text-sm text-slate-600">
                    No line yet: generate one with the routing engine, or follow a truck that drove
                    the lane well.
                  </p>
                )}
                {canChange && (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setTool(tool === 'generate' ? null : 'generate')}
                      aria-pressed={tool === 'generate'}
                      className={`inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-semibold ${
                        tool === 'generate'
                          ? 'bg-blue-700 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      <FaMagic size={11} aria-hidden="true" /> Generate by engine
                    </button>
                    <button
                      type="button"
                      onClick={() => setTool(tool === 'trip' ? null : 'trip')}
                      aria-pressed={tool === 'trip'}
                      className={`inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-semibold ${
                        tool === 'trip'
                          ? 'bg-slate-800 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      <FaHistory size={11} aria-hidden="true" /> Create from trip
                    </button>
                  </div>
                )}
                {canChange && tool === 'generate' && (
                  <GeneratePanel
                    vehicle={vehicle}
                    onVehicleChange={setVehicle}
                    busy={generating}
                    error={generateError}
                    last={lastGenerate}
                    onGenerate={() => runGenerate()}
                    via={via}
                    onViaChange={setVia}
                    addingVia={mapMode === 'via'}
                    onToggleAddingVia={() => setMapMode(mapMode === 'via' ? null : 'via')}
                    points={points}
                    pointMode={mapMode === 'from' || mapMode === 'to' ? mapMode : null}
                    onPointMode={(m) => setMapMode(m)}
                    onClearPoints={() => setPoints({ from: null, to: null })}
                  />
                )}
                {canChange && tool === 'trip' && (
                  <FromTripPanel lane={lane} onTrack={setTrack} onResult={onGoldenRun} />
                )}
              </Section>

              <Section
                title="Corridor"
                hint="Trucks inside the band are on route. The engine allows half the width each side."
              >
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    aria-label="Corridor width"
                    min={CORRIDOR_MIN_M}
                    max={2000}
                    step={25}
                    value={Math.min(2000, Number(draft.corridor_m) || CORRIDOR_MIN_M)}
                    onChange={(e) => set({ corridor_m: Number(e.target.value) })}
                    disabled={!canChange}
                    className="flex-1 accent-blue-700"
                  />
                  <input
                    type="number"
                    aria-label="Corridor width in metres"
                    min={CORRIDOR_MIN_M}
                    max={CORRIDOR_MAX_M}
                    step={25}
                    value={draft.corridor_m}
                    onChange={(e) =>
                      set({ corridor_m: e.target.value === '' ? '' : Number(e.target.value) })
                    }
                    disabled={!canChange}
                    className="w-20 px-2 py-1 border border-slate-200 rounded-lg text-sm tabular-nums"
                  />
                  <span className="text-xs text-slate-500">m</span>
                </div>
                <p className="text-xs text-slate-600 tabular-nums">
                  {Number(draft.corridor_m) || 0} m wide — {(Number(draft.corridor_m) || 0) / 2} m
                  each side of the route
                </p>
              </Section>

              <Section
                title="Required weighbridges"
                hint="A trip that passes one without a ticket is flagged. Click a zone on the map to toggle."
              >
                <GeofenceChecklist
                  items={weighbridges}
                  selected={draft.required_weighbridge_geofence_ids}
                  onToggle={(id) =>
                    set({
                      required_weighbridge_geofence_ids: toggleId(
                        draft.required_weighbridge_geofence_ids,
                        id
                      ),
                    })
                  }
                  disabled={!canChange}
                  empty="No weighbridge geofences yet (Sites & geofences)."
                />
              </Section>

              <Section
                title="Permitted stops"
                hint="Leaving the corridor into one of these is not a deviation (fuel, rest, depot)."
              >
                <GeofenceChecklist
                  items={stopCandidates}
                  selected={draft.permitted_stop_geofence_ids}
                  onToggle={(id) =>
                    set({
                      permitted_stop_geofence_ids: toggleId(draft.permitted_stop_geofence_ids, id),
                    })
                  }
                  disabled={!canChange}
                  empty="No other geofences yet."
                />
              </Section>

              <Section title="Plan">
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-xs text-slate-600">
                    Planned distance (km)
                    <input
                      className={inputClass}
                      inputMode="decimal"
                      value={draft.planned_km ?? ''}
                      onChange={(e) => set({ planned_km: e.target.value })}
                      disabled={!canChange}
                    />
                  </label>
                  <label className="block text-xs text-slate-600">
                    Planned time (min)
                    <input
                      className={inputClass}
                      inputMode="numeric"
                      value={draft.planned_min ?? ''}
                      onChange={(e) => set({ planned_min: e.target.value })}
                      disabled={!canChange}
                    />
                  </label>
                </div>
                {lastGenerate?.legs?.length > 1 && draft.source === 'engine' && (
                  <p className="text-xs text-slate-500 tabular-nums">
                    Per leg:{' '}
                    {lastGenerate.legs
                      .map((l) => `${formatKm(l.km)} / ${formatMinutes(l.min)}`)
                      .join(' · ')}
                  </p>
                )}
                <div>
                  <label htmlFor="route-risk" className="block text-xs text-slate-600">
                    Risk
                  </label>
                  <ThemedSelect
                    id="route-risk"
                    value={draft.risk || ''}
                    onChange={(e) => set({ risk: e.target.value })}
                    disabled={!canChange}
                  >
                    <option value="">Not rated</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </ThemedSelect>
                </div>
                {tollPlazas.length > 0 && (
                  <fieldset>
                    <legend className="text-xs text-slate-600 mb-1">
                      Toll plazas on the route
                    </legend>
                    <ul className="max-h-28 overflow-y-auto space-y-1">
                      {tollPlazas.map((t) => (
                        <li key={t.id}>
                          <label className="flex items-center gap-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              checked={draft.toll_plaza_ids.includes(t.id)}
                              onChange={() =>
                                set({
                                  toll_plaza_ids: draft.toll_plaza_ids.includes(t.id)
                                    ? draft.toll_plaza_ids.filter((x) => x !== t.id)
                                    : [...draft.toll_plaza_ids, t.id],
                                })
                              }
                              disabled={!canChange}
                            />
                            {t.name}
                          </label>
                        </li>
                      ))}
                    </ul>
                  </fieldset>
                )}
                {canChange && (
                  <label className="block text-xs text-slate-600">
                    Version note
                    <textarea
                      className={inputClass}
                      rows={2}
                      value={draft.notes}
                      onChange={(e) => set({ notes: e.target.value })}
                      maxLength={2000}
                      placeholder="What changed and why"
                    />
                  </label>
                )}
              </Section>

              {error && (
                <p
                  role="alert"
                  className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                >
                  <FaExclamationTriangle className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                  {error}
                </p>
              )}
              {notice && (
                <p
                  role="status"
                  className="flex items-start gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
                >
                  <FaCheckCircle className="mt-0.5 flex-shrink-0" aria-hidden="true" />
                  {notice}
                </p>
              )}
              <RecomputeOffer objectType="route" objects={route ? [route] : []} />

              {canChange && (
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || !dirty || (route && route.status === 'retired')}
                  className="w-full px-3 py-2.5 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
                >
                  {saving
                    ? 'Saving…'
                    : isNew
                      ? 'Create route'
                      : `Save as version ${(route?.current_version || 0) + 1}`}
                </button>
              )}
              {!canChange && !isNew && (
                <p className="text-xs text-slate-500">
                  {canManage
                    ? 'Only the organisation that owns this route can change it.'
                    : 'You can view this route; changing it needs the routes-manage permission.'}
                </p>
              )}
            </div>
          )}

          {tab === 'versions' && detail && (
            <VersionsPanel
              versions={detail.versions}
              currentVersion={route.current_version}
              compareVersion={compareVersion}
              onCompare={setCompareVersion}
              onStartFrom={startFromVersion}
              canChange={canChange}
            />
          )}

          {tab === 'advisories' && detail && (
            <AdvisoriesPanel
              route={{ ...route, corridor_m: base?.corridor_m }}
              lane={lane}
              advisories={detail.advisories}
              canChange={canPublishAdvisory}
              altVia={altVia}
              onAltViaChange={setAltVia}
              addingAltVia={mapMode === 'altVia'}
              onToggleAddingAltVia={() => setMapMode(mapMode === 'altVia' ? null : 'altVia')}
              altLine={altLine}
              onAltLine={setAltLine}
              onChanged={async () => {
                await load();
                onSaved();
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
